import React, { useMemo } from 'react';

function pct(part, total) {
  if (!total || total <= 0) return 0;
  return Math.min(100, Math.round((part / total) * 1000) / 10);
}

/** Bar rows for correspondence status (informational split only). */
export function CorrespondenceStatusChart({ summary }) {
  const s = summary || {};
  const open = Number(s.openChains ?? s.open ?? 0);
  const closed = Number(s.closedChains ?? s.closed ?? 0);
  const critical = Number(s.criticalChains ?? s.critical ?? 0);
  const total = Math.max(open + closed + critical, 1);
  return (
    <div className="sm-chart-card">
      <h4>Correspondence — subject status (counts)</h4>
      <BarRow label="Open" value={open} pct={pct(open, total)} />
      <BarRow label="Closed" value={closed} pct={pct(closed, total)} />
      <BarRow label="Critical" value={critical} pct={pct(critical, total)} accent />
    </div>
  );
}

/** RA gross / received / pending (crores display via formatKpiCrores in parent or raw numbers). */
export function RABillValueChart({ summary, formatVal }) {
  const s = summary || {};
  const gross = Number(s.grossValue ?? 0);
  const received = Number(s.receivedValue ?? 0);
  const pending = Number(s.pendingValue ?? 0);
  const total = Math.max(gross + received + pending, 1);
  const fmt = formatVal || ((v) => (Number.isFinite(v) ? v.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—'));
  return (
    <div className="sm-chart-card">
      <h4>RA bills — value split</h4>
      <BarRow label="Gross" value={fmt(gross)} pct={pct(gross, total)} />
      <BarRow label="Received" value={fmt(received)} pct={pct(received, total)} />
      <BarRow label="Pending" value={fmt(pending)} pct={pct(pending, total)} accent />
    </div>
  );
}

/** Project-wise total activity (subjects + entries proxy). */
export function ProjectActivityChart({ rows }) {
  const list = useMemo(() => {
    const r = Array.isArray(rows) ? rows : [];
    return r
      .map((p) => ({
        name: (p.projectName || '—').slice(0, 18),
        n: Number(p.totalChains ?? 0) + Number(p.totalEntries ?? 0),
      }))
      .sort((a, b) => b.n - a.n)
      .slice(0, 6);
  }, [rows]);
  const max = Math.max(...list.map((x) => x.n), 1);
  return (
    <div className="sm-chart-card">
      <h4>Project activity (subjects + entries)</h4>
      {list.length === 0 ? <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>No project rows.</p> : null}
      {list.map((row) => (
        <BarRow key={row.name} label={row.name} value={row.n} pct={pct(row.n, max)} />
      ))}
    </div>
  );
}

function BarRow({ label, value, pct, accent }) {
  return (
    <div className="sm-bar-row">
      <span className="sm-bar-label">{label}</span>
      <div className="sm-bar-track">
        <div className={`sm-bar-fill ${accent ? 'sm-bar-fill--accent' : ''}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="sm-bar-val">{value}</span>
    </div>
  );
}

export default CorrespondenceStatusChart;
